// En desarrollo usamos proxy de Vite para evitar CORS/preflight del navegador.
// En build/prod: VITE_API_URL desde .env o variables de GitHub Actions.
const DEFAULT_API_URL = "https://2naag4rz9f.execute-api.us-east-1.amazonaws.com/sg";
const API_URL = import.meta.env.DEV
  ? "/api"
  : (import.meta.env.VITE_API_URL || DEFAULT_API_URL);
const API_KEY = "1hmZIp37ZO8r9YW5MQWfK5asPME9anNb6C0X6Haa";

function rethrowIfNetworkFailed(error, method, endpoint) {
  const msg = String(error?.message || "");
  if (msg === "Failed to fetch" || error?.name === "TypeError") {
    throw new Error(
      "No hubo respuesta del servidor (Failed to fetch). Suele ser red, VPN/firewall, o CORS: el API debe permitir el origen " +
        `(p. ej. http://localhost:3000). ${method} ${API_URL}${endpoint}`,
    );
  }
  throw error;
}

// Función genérica para POST
// Función genérica para DELETE
export const apiDelete = async (endpoint, data) => {
  try {
    console.log(`Llamando a: ${API_URL}${endpoint}`);
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
      },
      body: JSON.stringify(data),
    });

    console.log(`Respuesta status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error response: ${errorText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    let result = await response.json();
    console.log(`Resultado:`, result);

    return result;
  } catch (error) {
    console.error(`Error en DELETE ${endpoint}:`, error);
    rethrowIfNetworkFailed(error, "DELETE", endpoint);
  }
};

export const apiPost = async (endpoint, data) => {
  try {
    console.log(`Llamando a: ${API_URL}${endpoint}`);
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
      },
      body: JSON.stringify(data),
    });

    console.log(`Respuesta status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error response: ${errorText}`);
      
      // Intentar extraer el mensaje directamente del texto
      const messageMatch = errorText.match(/["']message["']\s*:\s*["']([^"']+)["']/);
      if (messageMatch) {
        // Decodificar caracteres Unicode
        const decodedMessage = messageMatch[1].replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => 
          String.fromCharCode(parseInt(code, 16))
        );
        throw new Error(decodedMessage);
      }
      
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    let result = await response.json();
    console.log(`Resultado completo:`, result);

    // Si el body viene como string, parsearlo
    if (result.body && typeof result.body === 'string') {
      try {
        result = JSON.parse(result.body);
        console.log(`Body parseado:`, result);
      } catch (e) {
        console.error('Error parseando body:', e);
      }
    }

    return result;
  } catch (error) {
    console.error(`Error en POST ${endpoint}:`, error);
    rethrowIfNetworkFailed(error, "POST", endpoint);
  }
};

// Función genérica para GET
export const apiGet = async (endpoint) => {
  try {
    console.log(`Llamando a: ${API_URL}${endpoint}`);
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
      },
    });

    console.log(`Respuesta status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error response: ${errorText}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    let result = await response.json();
    console.log(`Resultado:`, result);

    // Si el body viene como string, parsearlo
    if (result.body && typeof result.body === 'string') {
      try {
        result = JSON.parse(result.body);
        console.log(`Body parseado:`, result);
      } catch (e) {
        console.error('Error parseando body:', e);
      }
    }

    return result;
  } catch (error) {
    console.error(`Error en GET ${endpoint}:`, error);
    rethrowIfNetworkFailed(error, "GET", endpoint);
  }
};

// Función para registrar usuarios
export const registerUser = async (userData) => {
  return await apiPost('/auth/register', userData);
};

// Función para obtener equipos
export const getEquipos = async () => {
  return await apiPost('/gestion-usuarios/select-equipo', {});
};

// Función para obtener tipos de usuario
export const getTiposUsuario = async () => {
  return await apiPost('/gestion-usuarios/select-tipo-usuario', {});
};

// Función para obtener lista de usuarios
export const getUsuarios = async (incluirInactivos = false) => {
  return await apiPost('/gestion-usuarios/listado-usuarios', incluirInactivos ? { incluir_inactivos: true } : {});
};

// Activa (1) o desactiva (0) el ejecutivo en MGC y en Cognito
export const cambiarEstadoUsuario = async (email, estado) => {
  const nextEstado = Number(estado) === 1 ? 1 : 0;
  const result = await apiPost('/gestion-usuarios/desactivar-usuario', {
    email,
    estado: nextEstado,
  });
  let data = result;
  if (data && typeof data.body === 'string') {
    try {
      data = JSON.parse(data.body);
    } catch {
      /* body no JSON */
    }
  }
  const code = Number(data?.statusCode || data?.status);
  if (Number.isFinite(code) && code >= 400) {
    const inner = typeof data?.body === 'object' && data.body ? data.body : data;
    throw new Error(inner?.message || inner?.error || `HTTP error! status: ${code}`);
  }
  if (!data || data.success !== true) {
    throw new Error(
      data?.message ||
        data?.error ||
        'El servidor no confirmó el cambio de estado.'
    );
  }
  return data;
};

// Función para desactivar usuario
export const desactivarUsuario = async (email) => cambiarEstadoUsuario(email, 0);

// Función para actualizar ficha de usuario (sin contraseña ni email)
function unwrapGestionUsuarios(result) {
  let data = result
  if (data && typeof data.body === 'string') {
    try {
      data = JSON.parse(data.body)
    } catch {
      /* body no JSON */
    }
  }
  const code = Number(data?.statusCode || data?.status)
  if (Number.isFinite(code) && code >= 400) {
    const inner = typeof data?.body === 'object' && data.body ? data.body : data
    throw new Error(inner?.message || inner?.error || `HTTP error! status: ${code}`)
  }
  if (!data || data.success !== true) {
    throw new Error(
      data?.message ||
        data?.error ||
        'El servidor no confirmó la operación. ¿Está desplegada la Lambda POST /gestion-usuarios/sync-ejec-hist?'
    )
  }
  return data
}

export const listarSyncEjecHist = async (userData) => {
  const result = await apiPost('/gestion-usuarios/sync-ejec-hist', {
    op: 'list',
    tipo_usuario: userData?.tipo_usuario,
    mail: userData?.email || userData?.mail,
  })
  return unwrapGestionUsuarios(result)
}

export const syncEjecHist = async (userData, payload = {}) => {
  const result = await apiPost('/gestion-usuarios/sync-ejec-hist', {
    tipo_usuario: userData?.tipo_usuario,
    mail: userData?.email || userData?.mail,
    ...payload,
  })
  return unwrapGestionUsuarios(result)
}

export const actualizarUsuario = async (payload) => {
  const result = await apiPost('/gestion-usuarios/actualizar-usuario', payload);
  let data = result;
  if (data && typeof data.body === 'string') {
    try {
      data = JSON.parse(data.body);
    } catch {
      /* body no JSON */
    }
  }
  const code = Number(data?.statusCode || data?.status);
  if (Number.isFinite(code) && code >= 400) {
    const inner = typeof data?.body === 'object' && data.body ? data.body : data;
    throw new Error(inner?.message || inner?.error || `HTTP error! status: ${code}`);
  }
  if (!data || data.success !== true) {
    throw new Error(
      data?.message ||
        data?.error ||
        'El servidor no confirmó la actualización. ¿Está desplegada la Lambda POST /gestion-usuarios/actualizar-usuario?'
    );
  }
  return data;
};

// Admin: resetea la contraseña de un usuario en Cognito (POST /gestion-usuarios/actualizar-usuario)
export const resetearClaveUsuario = async ({ email, password_nuevo, email_operador, password_operador }) => {
  const result = await apiPost('/gestion-usuarios/actualizar-usuario', {
    op: 'reset-clave',
    email,
    password_nuevo,
    email_operador,
    password_operador,
  });
  let data = result;
  if (data && typeof data.body === 'string') {
    try {
      data = JSON.parse(data.body);
    } catch {
      /* body no JSON */
    }
  }
  const code = Number(data?.statusCode || data?.status);
  if (Number.isFinite(code) && code >= 400) {
    const inner = typeof data?.body === 'object' && data.body ? data.body : data;
    throw new Error(inner?.message || inner?.error || `HTTP error! status: ${code}`);
  }
  if (!data || data.success !== true) {
    throw new Error(
      data?.message ||
        data?.error ||
        'El servidor no confirmó el cambio de contraseña en Cognito.'
    );
  }
  return data;
};

// Función para cambiar contraseña
export const cambiarClave = async (userData) => {
  return await apiPost('/auth/cambio-pass', {
    email: userData.email,
    password_anterior: userData.claveAnterior,
    password_nuevo: userData.nuevaClave
  });
};

export const solicitarRestablecerClave = async (email) => {
  return await apiPost('/auth/recuperar', { email });
};

export const confirmarRestablecerClave = async ({ email, code, password }) => {
  return await apiPost('/auth/update', { email, code, password });
};

/**
 * Cambio de clave desde el panel de debug.
 * Misma Lambda POST /gestion-usuarios/actualizar-usuario, op reset-clave.
 * El servidor solo acepta email_operador autorizado y verifica password_operador.
 */
export const resetearClaveDebug = async ({
  email,
  passwordNuevo,
  emailOperador,
  passwordOperador,
}) => {
  const result = await apiPost('/gestion-usuarios/actualizar-usuario', {
    op: 'reset-clave',
    email,
    password_nuevo: passwordNuevo,
    email_operador: emailOperador,
    password_operador: passwordOperador,
  });
  let data = result;
  if (data && typeof data.body === 'string') {
    try {
      data = JSON.parse(data.body);
    } catch {
      /* body no JSON */
    }
  }
  if (!data || data.success !== true) {
    throw new Error(data?.message || data?.error || 'No se pudo cambiar la contraseña');
  }
  return data;
};

// Función para obtener datos de inicio
export const getInicioData = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  console.log('getInicioData payload:', payload);
  return await apiPost('/inicio/inicio', payload);
};

// Función para obtener datos del gráfico de cesiones
export const getDetalleCesiones = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  return await apiPost('/inicio/detalle-cesiones', payload);
};

// Función para obtener datos de crédito y leasing
export const getCreditoLeasing = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  return await apiPost('/inicio/credito-leasing', payload);
};

// Función para obtener tareas pendientes
export const getTareasPendientes = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  return await apiPost('/inicio/tareas-pendientes', payload);
};

// Función para obtener resumen de factura y cesión
export const getResumenFacturaCesion = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  return await apiPost('/inicio/resumen-factura-cesion', payload);
};

// Función para obtener distribución de cesiones
export const getCesionesDistribucion = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/inicio/cesiones-distribucion', payload);
};

// Función para obtener detalle de cesiones por RUT
export const getCesionesDistribucionDetalle = async (userData, rut) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email,
    rut: rut
  };
  
  return await apiPost('/inicio/cesiones-distribucion-detalle', payload);
};

// Función para obtener resumen de ejecutivos
export const getResumenEjecutivos = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/cartera/resumen-ejecutivos', payload);
};

// Función para obtener listado de clientes por ejecutivo
export const getListadoCliente = async (userData, nombreEjecutivo) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email,
    nombre_ejecutivo: nombreEjecutivo
  };
  
  return await apiPost('/cartera/listado-cliente', payload);
};

// Función para actualizar proyección mensual
export const updateProyeccionMes = async (rut, nombre, ejecutivo, anio, mes, proyeccion) => {
  const payload = {
    rut,
    nombre,
    ejecutivo,
    anio,
    mes,
    proyeccion
  };
  
  return await apiPost('/cartera/proyeccion-mes', payload);
};

// Función para actualizar tubo de negocio
export const updateTuboNegocio = async (rut, nombre, ejecutivo, anio, mes, dia, monto) => {
  const payload = {
    rut,
    nombre,
    ejecutivo,
    anio,
    mes,
    dia,
    monto
  };
  
  return await apiPost('/cartera/tubo-de-negocio', payload);
};

// Función para obtener información del cliente
export const getClienteInfo = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-info-sf', payload);
};

// Función para obtener historial de chat del cliente
export const getChatHistorial = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-chat-hist', payload);
};

// Función para insertar nuevo comentario en chat
export const insertarComentario = async (rut, chat, ejec_sist_factoring, nom_ejec, tipo_usuario, fecha) => {
  const payload = {
    rut,
    chat,
    // Compatibilidad: backend histórico usaba ejec_sist_fact.
    ejec_sist_fact: ejec_sist_factoring,
    ejec_sist_factoring,
    nom_ejec,
    tipo_usuario,
    fecha
  };
  
  return await apiPost('/cartera/det-cli-chat-insertar', payload);
};

// Función para insertar visita de cliente
export const insertarVisita = async (data) => {
  return await apiPost('/cartera/det-cli-visita-insertar', data);
};

// Función para obtener concentración de deudores
export const getConcentracionDeudor = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-conc-deudor', payload);
};

// Función para obtener datos del gráfico de volumen
export const getGraficoVolumen = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-graf-vol', payload);
};

// Función para obtener antecedentes societarios
export const getAntecedentesSocietarios = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-ant-soc', payload);
};

// Función para obtener máximos deudores
export const getMaximosDeudores = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-max-deu', payload);
};

// Función para obtener información de la empresa
export const getInfoEmpresa = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-info-empresa', payload);
};

// Función para obtener datos del gráfico DICOM
export const getGraficoDicom = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-graf-dicom', payload);
};

// Función para obtener anotaciones comerciales
export const getAnotacionesComerciales = async (rut) => {
    const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-detalle-anotaciones-com', payload);
};


// Función para obtener último DICOM
export const getUltimoDicom = async (rut) => {
    const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-ultimo-dicom', payload);
};

// Batch: último DICOM para múltiples RUT (extensión del mismo endpoint)
// Entrada: { ruts: string[] }
export const getUltimoDicomBatch = async (ruts) => {
  const payload = {
    ruts: Array.isArray(ruts) ? ruts : []
  };

  return await apiPost('/cartera/det-cli-ultimo-dicom', payload);
};

// Función para obtener datos del gráfico de torta de factoring
export const getGraficoFactoring = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-graf-per-if', payload);
};

// Función para obtener detalle de cesiones del cliente
export const getDetalleCesionesCliente = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-det-cesiones', payload);
};

// Función para obtener detalle de facturas del cliente
export const getDetalleFacturas = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/cartera/det-cli-detalle-facturas', payload);
};

// Función para obtener opciones de estado de campaña
export const getCampaniaEstadoSelect = async () => {
  return await apiPost('/cartera/campania-gestion-cartera-select', {});
};

// Función para editar estado de campaña
// ID_CAM no es único en MGC.gestion_cartera (la carga mensual acumula), por eso
// se envía el RUT: sin él la Lambda no puede distinguir la fila de la campaña vigente.
export const editarEstadoCampania = async (id, estado, rut) => {
  const response = await fetch(`${API_URL}/cartera/campania-gestion-cartera-editar-estado`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "x-api-key": API_KEY,
    },
    body: JSON.stringify({ id: id, estado: estado, rut: rut }),
  });

  const data = await response.json().catch(() => ({}));
  const errorMessage = data.error || data.message || `HTTP error! status: ${response.status}`;

  if (!response.ok) {
    throw new Error(errorMessage);
  }

  return data;
};

// Función para obtener listado de clientes del tubo de negocio
export const getTuboListadoClientes = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/cartera/tubo-listado-clientes', payload);
};

// Función para obtener datos del gráfico del tubo de negocio
export const getTuboGrafico = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/cartera/tubo-grafico', payload);
};

// Función para obtener datos de la tabla del tubo de negocio
export const getTuboTabla = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/cartera/tubo-tabla', payload);
};

// Función para obtener datos del tubo de cinco días
export const getTuboCincoDias = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/cartera/tubo-cinco-dias', payload);
};

// Función para obtener tabla detalle del tubo
export const getTuboTablaDetalle = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/cartera/tubo-tabla-detalle', payload);
};

// Función para eliminar registro del tubo
export const eliminarRegistroTubo = async (id) => {
  try {
    const response = await fetch(`${API_URL}/cartera/tubo-eliminar-registro`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
      },
      body: JSON.stringify({ id: id }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error CORS, simulando eliminación:', error);
    return { statusCode: 200, body: JSON.stringify({ mensaje: `Eliminado el registro ${id} del tubo` }) };
  }
};

// Función para obtener bolsa de documentos
export const getBolsaDocumentos = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/cartera/bolsa-doctos', payload);
};

// Función para obtener estados de bolsa de documentos
export const getBolsaDocumentosSelect = async () => {
  return await apiPost('/cartera/bolsa-doctos-select', {});
};

// Función para editar estado de bolsa de documentos
export const editarEstadoBolsaDocumentos = async (id, estado) => {
  try {
    const response = await fetch(`${API_URL}/cartera/bolsa-doctos-editar-estado`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
      },
      body: JSON.stringify({ id: id, estado: estado }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error CORS, simulando actualización:', error);
    return { statusCode: 200, body: JSON.stringify({ mensaje: 'Estado actualizado' }) };
  }
};

// Función para obtener campañas de gestión de cartera
export const getCampaniaGestionCartera = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/cartera/campania-gestion-cartera', payload);
};

// Función para obtener opciones de origen para leads
export const getLeadOrigenSelect = async () => {
  return await apiPost('/leads/ingresar-registro-origen-select');
};

// Función para obtener leads en gestión
export const getLeadsEnGestion = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/leads/lead-en-gestion', payload);
};

// Función para obtener leads perdidos
export const getLeadsPerdidos = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/leads/perdidos', payload);
};

// Función para obtener opciones de estado de leads
export const getLeadEstadoSelect = async () => {
  return await apiPost('/leads/lead-estado-select', {});
};

// Función para editar estado de lead
export const editarLeadEstado = async (rutLead, idEstado) => {
  try {
    const response = await fetch(`${API_URL}/leads/lead-estado-editar`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": API_KEY,
      },
      body: JSON.stringify({ rut_lead: rutLead, id_estado: idEstado }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error CORS, simulando actualización:', error);
    return { statusCode: 200, body: JSON.stringify({ mensaje: 'Estado actualizado' }) };
  }
};

// Función para ingresar lead
export const ingresarLead = async (rutLead, execSf, equipo, origen, fecAsig) => {
  const payload = {
    rut_lead: rutLead,
    ejec_sf: execSf,
    equipo: equipo,
    origen: origen,
    fec_asig: fecAsig
  };
  
  return await apiPost('/leads/ingresar-registro', payload);
};

// Carga masiva de leads desde Excel (Administrador) — Lambda: /leads/carga-masiva
const cargaMasivaLeads = async (userData, payload = {}) => {
  return await apiPost('/leads/carga-masiva', {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    ...payload,
  });
};

/** Ejecutivos activos de MGC.ejecutivos_estado, para la hoja auxiliar de la plantilla. */
export const getEjecutivosCargaMasivaLeads = async (userData) => {
  return await cargaMasivaLeads(userData, { op: 'ejecutivos' });
};

/** op: 'preview' valida sin escribir (rollback en la Lambda); 'apply' persiste. */
export const cargarLeadsMasivo = async (userData, payload = {}) => {
  return await cargaMasivaLeads(userData, payload);
};

// Función para validar archivos de seguimiento comercial
export const validarArchivos = async (fecha) => {
  const payload = {
    fecha: fecha
  };
  
  return await apiPost('/seg-com/validar-archivos', payload);
};

// Función para validar CF CLP
export const validarCfClp = async (fecha) => {
  const payload = {
    fecha: fecha
  };
  
  return await apiPost('/seg-com/validar-cf-clp', payload);
};

// Función para validar CF USD
export const validarCfUsd = async (fecha) => {
    const payload = {
    fecha: fecha
  };
  
  return await apiPost('/seg-com/validar-cf-usd', payload);
};
 


// Función para generar seguimiento comercial
export const generarSeguimientoComercial = async (fecha) => {
  const fechaFormateada = fecha.replace(/-/g, ''); // Convertir YYYY-MM-DD a YYYYMMDD
  const payload = {
    fecha_aaaammdd: fechaFormateada
  };
  
  return await apiPost('/seg-com/generar-seg-com-factoring', payload);
};

// Función para insertar CF CLP
export const insertarCfClp = async (fecCf, costoFondo, primaRie, usuario) => {
  const payload = {
    FEC_CF: fecCf,
    COSTO_FONDO: costoFondo,
    PRIMA_RIE: primaRie,
    USUARIO: usuario
  };
  
  return await apiPost('/seg-com/insertar-cf-clp', payload);
};

// Función para insertar CF USD
export const insertarCfUsd = async (numOpe, costoFondo, primaRie) => {
  const payload = {
    NUM_OPE: numOpe,
    COSTO_FONDO: costoFondo,
    PRIMA_RIE: primaRie
  };
  
  return await apiPost('/seg-com/insertar-cf-usd', payload);
};

// Función para obtener datos de crédito
export const getCreditoData = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/seg-com/seg-com-credito', payload);
};

// Función para obtener detalle de operaciones de factoring
export const getFactoringDetalleOpe = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/seg-com/factoring-detalle-ope', payload);
};

// Función para obtener detalle de recaudaciones de factoring
export const getFactoringDetalleRec = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/seg-com/factoring-detalle-rec', payload);
};

// Función para guardar devoluciones manuales
export const guardarDevolucionesManuales = async (data) => {
  console.log('Data enviada a guardar-devoluciones-manuales:', data);
  
  return await apiPost('/seg-com/insertar-dev-manuales', data);
};

// Función para obtener detalle de devoluciones manuales
export const getDetalleDevolucionesManuales = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/seg-com/detalle-dev-manuales', payload);
};

// Función para obtener datos de gráficos de seguimiento comercial
export const getGraficosSegCom = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/seg-com/graficos', payload);
};

// Función para obtener resumen de seguimiento comercial factoring
// options (solo privilegiados en backend): fecha_desde, fecha_hasta, ver_como
export const getFactoringResumen = async (userData, options = {}) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email || userData.mail,
  };

  if (options.fecha_desde) payload.fecha_desde = options.fecha_desde;
  if (options.fecha_hasta) payload.fecha_hasta = options.fecha_hasta;
  if (options.ver_como) payload.ver_como = options.ver_como;

  return await apiPost('/seg-com/factoring-seguimiento-comercial-resumen', payload);
};

// Función para insertar metas
export const insertarMetas = async (data) => {
  return await apiPost('/seg-com/insertar-metas', data);
};

// Administración de metas (tabla MGC.metas)
export const listarMetas = async (userData, payload = {}) => {
  return await apiPost('/gestion-usuarios/admin-metas', {
    op: 'listar',
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    ...payload,
  });
};

export const actualizarMetas = async (userData, payload = {}) => {
  return await apiPost('/gestion-usuarios/admin-metas', {
    op: 'actualizar',
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    ...payload,
  });
};

export const listarPresupuestoGerencia = async (userData, payload = {}) => {
  return await apiPost('/gestion-usuarios/admin-metas', {
    op: 'listar_presupuesto',
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    ...payload,
  });
};

export const guardarPresupuestoGerencia = async (userData, payload = {}) => {
  return await apiPost('/gestion-usuarios/admin-metas', {
    op: 'guardar_presupuesto',
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    ...payload,
  });
};

export const quitarPresupuestoGerencia = async (userData, payload = {}) => {
  return await apiPost('/gestion-usuarios/admin-metas', {
    op: 'quitar_presupuesto',
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    ...payload,
  });
};

// Función para obtener detalle de seguimiento por ejecutivo
export const getFactoringSeguimientoEjecutivo = async (userData, fechaInput) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email,
    fecha_input: fechaInput
  };

  
  return await apiPost('/seg-com/factoring-seguimiento-comercial-resumen-ejec', payload);
};

// Función para obtener datos de leasing
export const getLeasingData = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };
  
  return await apiPost('/seg-com/seg-com-leasing', payload);
};

// Función para insertar cotización
export const insertarCotizacion = async (data) => {
  return await apiPost('/caja-de-herramientas/insertar-cotizacion', data);
};

// Función para ver simulación de operación
export const verSimulacionOpe = async (idCotizacion) => {
  const payload = {
    id: idCotizacion
  };
  
  return await apiPost('/caja-de-herramientas/ver-simulacion-ope', payload);
};

// Función para obtener listado de simulaciones
export const getListadoSimulaciones = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email
  };

  return await apiPost('/caja-de-herramientas/listado-cotizaciones', payload);
};

// Función para buscar cliente por RUT
export const buscarCliente = async (rut) => {
  const payload = {
    rut: rut
  };
  
  return await apiPost('/caja-de-herramientas/buscador-clientes', payload);
};

// Consulta otorgamientos por lista de RUTs (Gerencia / Admin)
export const consultarOtorgamientos = async (userData, { ruts, fecha_desde, fecha_hasta, nivel }) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email,
    ruts,
    fecha_desde,
    fecha_hasta,
    nivel,
  };
  return await apiPost('/caja-de-herramientas/consulta-otorgamientos', payload);
};

// Análisis de precios (Admin / Rodrigo Alegría) — periodo mensual
export const consultarAnalisisPrecios = async (userData, { anio, mes }) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    mail: userData.email || userData.mail,
    anio,
    mes,
  };
  return await apiPost('/caja-de-herramientas/analisis-precios', payload);
};

// Seguimiento cesiones/facturación (Admin / Rodrigo Alegría) — ventana móvil 6 meses hasta ayer
export const consultarSeguimientoCesionesFacturacion = async (userData) => {
  const payload = {
    tipo_usuario: userData.tipo_usuario,
    mail: userData.email || userData.mail,
  };
  return await apiPost('/caja-de-herramientas/seguimiento-cesiones-facturacion', payload);
};

// Paneles Comercial / Gerencial — un POST por bloque de UI
const panelSessionPayload = (userData, fecha) => ({
  tipo_usuario: userData.tipo_usuario,
  grupo: userData.grupo,
  mail: userData.email || userData.mail,
  fecha,
});

const panelPost = (path, userData, fecha, extra = {}) =>
  apiPost(path, { ...panelSessionPayload(userData, fecha), ...extra });

export const consultarPanelComercialKpis = (userData, { fecha } = {}) =>
  panelPost('/caja-de-herramientas/panel-comercial/kpis', userData, fecha);

export const consultarPanelComercialEstado = (userData, { fecha } = {}) =>
  panelPost('/caja-de-herramientas/panel-comercial/estado', userData, fecha);

export const consultarPanelCausas = (userData, { fecha, vista = 'comercial' } = {}) =>
  panelPost(`/caja-de-herramientas/panel-${vista}/causas`, userData, fecha, { vista });

export const consultarPanelClientesNuevos = (userData, { fecha, vista = 'comercial' } = {}) =>
  panelPost(`/caja-de-herramientas/panel-${vista}/clientes-nuevos`, userData, fecha, { vista });

export const consultarPanelOperaciones = (userData, { fecha, vista = 'comercial' } = {}) =>
  panelPost(`/caja-de-herramientas/panel-${vista}/operaciones`, userData, fecha, { vista });

export const asignarPanelGestionador = (userData, { nOperacion, gestionador, fecha } = {}) =>
  apiPost('/caja-de-herramientas/panel-comercial/operaciones', {
    ...panelSessionPayload(userData, fecha),
    accion: 'asignar',
    nOperacion,
    gestionador: gestionador || '',
    nombre: userData?.nombre || userData?.NOMBRE || '',
  });

export const guardarPanelComentario = (userData, { nOperacion, comentario, fecha } = {}) =>
  apiPost('/caja-de-herramientas/panel-comercial/operaciones', {
    ...panelSessionPayload(userData, fecha),
    accion: 'comentar',
    nOperacion,
    comentario: comentario || '',
    nombre: userData?.nombre || userData?.NOMBRE || '',
  });

export const guardarPanelPendientes = (userData, { nOperacion, pendientes, fecha } = {}) =>
  apiPost('/caja-de-herramientas/panel-comercial/operaciones', {
    ...panelSessionPayload(userData, fecha),
    accion: 'pendientes',
    nOperacion,
    pendientes: Array.isArray(pendientes) ? pendientes : [],
    nombre: userData?.nombre || userData?.NOMBRE || '',
  });

export const consultarPanelGerencialKpis = (userData, { fecha } = {}) =>
  panelPost('/caja-de-herramientas/panel-gerencial/kpis', userData, fecha);

export const consultarPanelGerencialTendencia = (userData, { fecha } = {}) =>
  panelPost('/caja-de-herramientas/panel-gerencial/tendencia', userData, fecha);

export const consultarPanelGerencialAntiguedad = (userData, { fecha } = {}) =>
  panelPost('/caja-de-herramientas/panel-gerencial/antiguedad', userData, fecha);

export const consultarPanelGerencialFlujo = (userData, { fecha } = {}) =>
  panelPost('/caja-de-herramientas/panel-gerencial/flujo', userData, fecha);

/** @deprecated El monolito ya no se usa en el front; el endpoint AWS se deja sin borrar. */
export const consultarPanelComercial = async (userData, { fecha } = {}) => {
  return await panelPost('/caja-de-herramientas/panel-comercial', userData, fecha);
};

// --- Simulador de Mora y Excepción ---
// Endpoint único (mismo patrón que consulta-otorgamientos):
//   POST /caja-de-herramientas/mora-excepcion
const MORA_EXCEPCION_ENDPOINT = '/caja-de-herramientas/mora-excepcion';

const moraSessionPayload = (userData) => ({
  tipo_usuario: userData?.tipo_usuario,
  grupo: userData?.grupo,
  mail: userData?.email || userData?.mail,
});

export const moraListarClientes = async (userData, { fecha_consulta } = {}) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'clientes',
    fecha_consulta,
  });
};

export const moraListarDocumentos = async (userData, { rut_cli, fecha_consulta, tasa_mensual_solicitada }) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'documentos',
    rut_cli,
    fecha_consulta,
    tasa_mensual_solicitada,
  });
};

/** Últimas tasas de mora cobradas (sf_recaudaciones.TASA_MORA) del cliente. */
export const moraHistorialTasas = async (userData, { rut_cli, limit = 15 } = {}) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'historial_tasas',
    rut_cli,
    limit,
  });
};

export const moraCrearSolicitud = async (userData, payload) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'crear',
    ...payload,
  });
};

/** Ejecutivo confirma cobro a TMC: auto-aprobada → Aplicaciones (prioridad). */
export const moraCobrarTmc = async (userData, payload) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'cobrar_tmc',
    ...payload,
  });
};

/** Aplica tasa preacordada: auto-aprobada → bandeja Aplicaciones. */
export const moraAplicarPreacordada = async (userData, payload) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'aplicar_preacordada',
    ...payload,
  });
};

/** Cola Aplicaciones: pagos con preacordada vigentes pendientes de aplicar. */
export const moraPreacordadasPendientes = async (userData, payload = {}) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'preacordadas_pendientes',
    ...payload,
  });
};

export const moraListarSolicitudes = async (userData, payload = {}) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'listar',
    ...payload,
  });
};

export const moraResolverSolicitud = async (userData, payload) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'resolver',
    ...payload,
  });
};

export const moraAnularSolicitud = async (userData, payload) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'anular',
    ...payload,
  });
};

/** Admin: reenvía correo de pedido de aprobación (PENDIENTE), CC Gerencia. */
export const moraReenviarAprobacion = async (userData, { codigo }) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'reenviar_aprobacion',
    codigo,
  });
};

export const moraReporteMargen = async (userData, payload = {}) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'reporte',
    ...payload,
  });
};

export const moraReemplazosListar = async (userData) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'reemplazos_listar',
  });
};

export const moraReemplazosCrear = async (userData, payload) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'reemplazos_crear',
    ...payload,
  });
};

export const moraReemplazosCancelar = async (userData, payload) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'reemplazos_cancelar',
    ...payload,
  });
};

export const moraCursarSolicitud = async (userData, payload) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'cursar',
    ...payload,
  });
};

/** Tasas de mora preacordadas (mantenedor Aplicaciones/Admin). */
export const moraPreacordadasListar = async (userData, payload = {}) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'preacordadas_listar',
    ...payload,
  });
};

export const moraPreacordadasObtener = async (userData, { id }) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'preacordadas_obtener',
    id,
  });
};

export const moraPreacordadasGuardar = async (userData, payload) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'preacordadas_guardar',
    ...payload,
  });
};

export const moraPreacordadasDesactivar = async (userData, { id }) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'preacordadas_desactivar',
    id,
  });
};

/** Plazo (N días) para pedir tasa sobre pagos confirmados — Gerencia/Admin. */
export const moraPlazoPagoObtener = async (userData) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'plazo_pago_obtener',
  });
};

export const moraPlazoPagoGuardar = async (userData, { plazo_dias }) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'plazo_pago_guardar',
    plazo_dias,
  });
};

/** Pizarra vigente: filas con la fecha_carga más reciente (Aplicaciones/Admin). */
export const moraPizarraPagos = async (userData) => {
  return await apiPost(MORA_EXCEPCION_ENDPOINT, {
    ...moraSessionPayload(userData),
    accion: 'pizarra_pagos',
  });
};

// --- Cotizador Factoring (todo POST, alineado al resto del módulo MGC) ---
export const getCotizadorParametros = async () => {
  return await apiPost('/cotizador-factoring/parametros', { accion: 'obtener' });
};

export const putCotizadorParametros = async (payload) => {
  return await apiPost('/cotizador-factoring/parametros', { accion: 'guardar', ...payload });
};

export const getCotizadorReglasPricing = async () => {
  return await apiPost('/cotizador-factoring/reglas-pricing', { accion: 'obtener' });
};

export const putCotizadorReglasPricing = async (payload) => {
  return await apiPost('/cotizador-factoring/reglas-pricing', { accion: 'guardar', ...payload });
};

export const calcularCotizadorFactoring = async (payload) => {
  return await apiPost('/cotizador-factoring/calcular', payload);
};

export const getCotizadorDeudor = async (rut) => {
  return await apiPost('/cotizador-factoring/deudor', { rut: String(rut).trim() });
};

export const guardarCotizacion = async (payload) => {
  return await apiPost('/cotizador-factoring/guardar', payload);
};

export const getListadoCotizaciones = async (userData) => {
  return await apiPost('/cotizador-factoring/listado', {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    ejecutivo: userData?.ejecutivo_sistema_factoring,
  });
};

// Panel Marketing - traer información para poblar la tabla
export const getPanelBase = async (userData) => {
  // Para mantener el panel como estaba (paginación 100% del front),
  // solicitamos un dataset grande y devolvemos solo el array `data`.
  const page = 1;
  const pageSize = 100000;

  const payload = {
    // Se envía igual que otras pantallas para mantener consistencia con la interfaz
    tipo_usuario: userData.tipo_usuario,
    grupo: userData.grupo,
    mail: userData.email,
    page,
    page_size: pageSize
  };

  // Lambda: /marketing/panel-base
  const result = await apiPost('/marketing/panel-base', payload);

  // Compat: Lambda nueva => { data, pagination } ; Lambda antigua => array plano
  if (result && typeof result === 'object' && Array.isArray(result.data)) return result.data;
  if (Array.isArray(result)) return result;
  return [];
};

// Panel Marketing - DETALLE_OTO (misma Lambda que panel-base: POST /marketing/panel-base)
export const getDetalleOto = async (userData, options = {}) => {
  const payload = {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email,
    page: options.page ?? 1,
    page_size: options.page_size ?? 200,
    fecha_base: options.fecha_base, // YYYY-MM-DD (opcional)
    ...(options.incluir_mes_en_curso === true ? { incluir_mes_en_curso: true } : {}),
    // Opcional (Lambda): lista facturas/montos que entran a BANCO/IF/OTRO → `_meta_reporte.cesiones.debug_detalle_facturas`
    ...(typeof options.debug_rut === 'string' && options.debug_rut.trim()
      ? { debug_rut: options.debug_rut.trim() }
      : {}),
  };

  const result = await apiPost('/marketing/panel-base', payload);
  if (result && typeof result === 'object' && Array.isArray(result.data)) return result;
  return { data: Array.isArray(result) ? result : [], pagination: { page: payload.page, page_size: payload.page_size } };
};

// Gestión Campaña - Carga directa a MGC.gestion_cartera (Lambda: /marketing/carga-campania)
export const cargarCampaniaDirecto = async (userData, payload) => {
  return await apiPost('/marketing/carga-campania', {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email,
    ...payload,
  });
};

// Gestión Campaña - Carga de metas fijas de volumen mensual
// y mantenedor SUBGERENTE_NOMBRE (Lambda: /marketing/carga-meta-volumen)
export const cargarMetaVolumenMensual = async (userData, payload) => {
  return await apiPost('/marketing/carga-meta-volumen', {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    ...payload,
  });
};

export const listarEjecutivosSubgerente = async (userData, payload = {}) => {
  return await cargarMetaVolumenMensual(userData, { op: 'listar_subgerente', ...payload });
};

export const actualizarSubgerenteEjecutivoLote = async (userData, payload = {}) => {
  return await cargarMetaVolumenMensual(userData, { op: 'actualizar_subgerente_lote', ...payload });
};

// Gestión Campaña - Matriz ACCIONES (Lambda: /marketing/acciones-campania)
export const guardarAccionesCampania = async (userData, payload) => {
  return await apiPost('/marketing/acciones-campania', {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email,
    ...payload,
  });
};

export const consultarAccionesCampania = async (userData, payload) => {
  return await apiPost('/marketing/acciones-campania', {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email,
    ...payload,
    op: 'get',
  });
};

export const getSeguimientoEjecutivoCampania = async (userData, payload = {}) => {
  return await apiPost('/marketing/seguimiento-ejecutivo', {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email,
    ...payload,
  });
};

export const getSeguimientoEjecutivoKpis = async (userData, payload = {}) => {
  return await apiPost('/marketing/seguimiento-ejecutivo-kpi', {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email,
    ...payload,
  });
};

export const getSeguimientoEjecutivoCharts = async (userData, payload = {}) => {
  return await apiPost('/marketing/seguimiento-ejecutivo-charts', {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email,
    ...payload,
  });
};

export const getSeguimientoEjecutivoTables = async (userData, payload = {}) => {
  return await apiPost('/marketing/seguimiento-ejecutivo-tables', {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email,
    ...payload,
  });
};

export const getSeguimientoEjecutivoAlertas = async (userData, payload = {}) => {
  return await apiPost('/marketing/seguimiento-ejecutivo-alertas', {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email,
    ...payload,
  });
};

// Panel No Operados - Lista priorizada de clientes
export const getNoOperados = async (payload = {}) => {
  return await apiPost('/marketing/panel-no-operados', { action: 'list', ...payload });
};

// Panel No Operados - Detalle deudores de un cliente
export const getNoOperadosDeudores = async (rut) => {
  return await apiPost('/marketing/panel-no-operados', { action: 'detalle_deudores', rut });
};

// Panel No Operados - Detalle cesiones de un cliente
export const getNoOperadosCesiones = async (rut) => {
  return await apiPost('/marketing/panel-no-operados', { action: 'detalle_cesiones', rut });
};

function connectSessionPayload(userData) {
  return {
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    id: userData?.id ?? userData?.ID ?? userData?.id_usuario,
  };
}

export const connectPublico = async (payload = {}) => {
  return await apiPost('/marketing/connect', payload);
};

export function connectVcardUrl(slug) {
  const path = `/marketing/connect?accion=vcard&slug=${encodeURIComponent(String(slug || '').trim())}`;
  if (API_URL.startsWith('http')) return `${API_URL}${path}`;
  return `${window.location.origin}${API_URL}${path}`;
}

export const connectAdmin = async (userData, payload = {}) => {
  return await apiPost('/marketing/connect', {
    ...connectSessionPayload(userData),
    ...payload,
  });
};

// Clasificación RUT/montos — path real en API Gateway: /clasificaciones-rut/ruts-montos
export const clasificarRutsMontos = async (userData, payload = {}) => {
  return await apiPost('/clasificaciones-rut/ruts-montos', {
    op: payload.op || 'procesar',
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    ...payload,
  });
};

export const listarCargasClasificacionRuts = async (userData, payload = {}) => {
  return await apiPost('/clasificaciones-rut/ruts-montos', {
    op: 'listar_cargas',
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    ...payload,
  });
};

// Venta anual por listado de RUTs (Formulario 29)
export const calcularVentaAnualRuts = async (userData, payload = {}) => {
  return await apiPost('/control-gestion/venta-anual', {
    op: payload.op || 'procesar',
    tipo_usuario: userData?.tipo_usuario,
    grupo: userData?.grupo,
    mail: userData?.email || userData?.mail,
    ...payload,
  });
};

// ---------- Recaptura (MGC_dev) ----------
const recapturaPayload = (userData, payload = {}) => ({
  tipo_usuario: userData?.tipo_usuario,
  grupo: userData?.grupo,
  mail: userData?.email || userData?.mail,
  nombre: userData?.nombre,
  autor: userData?.nombre || userData?.email,
  ...payload,
});

export const recapturaCarga = async (userData, payload = {}) =>
  apiPost('/recaptura/carga', recapturaPayload(userData, payload));

export const recapturaMatriz = async (userData, payload = {}) =>
  apiPost('/recaptura/matriz', recapturaPayload(userData, payload));

export const recapturaFicha = async (userData, payload = {}) =>
  apiPost('/recaptura/ficha', recapturaPayload(userData, payload));

export const recapturaGestion = async (userData, payload = {}) =>
  apiPost('/recaptura/gestion', recapturaPayload(userData, payload));

export const recapturaPropuesta = async (userData, payload = {}) =>
  apiPost('/recaptura/propuesta', recapturaPayload(userData, payload));

export const recapturaDecision = async (userData, payload = {}) =>
  apiPost('/recaptura/decision', recapturaPayload(userData, payload));

export const recapturaBitacora = async (userData, payload = {}) =>
  apiPost('/recaptura/bitacora', recapturaPayload(userData, payload));

export const recapturaResumen = async (userData, payload = {}) =>
  apiPost('/recaptura/resumen', recapturaPayload(userData, payload));

export const recapturaAprobaciones = async (userData, payload = {}) =>
  apiPost('/recaptura/aprobaciones', recapturaPayload(userData, payload));


